import React, { useState, useMemo, useRef, useEffect, useContext } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown, X } from 'lucide-react';
import { cn } from '../lib/utils';
import { FilterSortContext } from '../lib/FilterSortProvider';

const DataTable = ({
  data = [],
  columns = [],
  onRowClick,
}) => {
  const {
    searchQuery,
    filters,
    filterLogic,
    sortConfigs,
    setSortConfigs,
    pageSize,
    setPageSize,
  } = useContext(FilterSortContext);

  const [activePopup, setActivePopup] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // The columns considered "dates"
  const dateColumns = ['est_delivery', 'atd', 'ata', 'dest_eta', 'created'];

  // Sorting
  const handleSort = (key) => {
    const configIndex = sortConfigs.findIndex((config) => config.key === key);
    let newSortConfigs = [...sortConfigs];

    if (configIndex === -1) {
      // Not sorted yet => ascending
      newSortConfigs.push({
        key,
        direction: 'ascending',
        order: newSortConfigs.length + 1,
      });
    } else {
      const currentDirection = sortConfigs[configIndex].direction;
      if (currentDirection === 'ascending') {
        newSortConfigs[configIndex] = {
          ...newSortConfigs[configIndex],
          direction: 'descending',
        };
      } else if (currentDirection === 'descending') {
        // remove sorting for this column
        newSortConfigs = newSortConfigs.filter((c) => c.key !== key);
        // reassign order
        newSortConfigs = newSortConfigs.map((c, idx) => ({
          ...c,
          order: idx + 1,
        }));
      }
    }

    setSortConfigs(newSortConfigs);
  };

  const handleRowClickInternal = (rowIndex, event) => {
    // If user clicked something with data-has-parts="true", skip row navigation
    if (event.target.closest('[data-has-parts="true"]')) return;
    if (!onRowClick) return;
    const actualIndex = (currentPage - 1) * pageSize + rowIndex;
    const actualItem = sortedData[actualIndex];
    onRowClick(actualItem, actualIndex, sortedData);
  };

  const handlePartsClick = (rowIndex, event) => {
    event.stopPropagation();
    if (activePopup && activePopup.rowIndex === rowIndex) {
      setActivePopup(null);
    } else {
      const rect = event.currentTarget.getBoundingClientRect();
      const popupWidth = 320;
      const popupHeight = 400;
      let left = rect.right + window.scrollX + 10;
      let top = rect.top + window.scrollY;

      if (left + popupWidth > window.innerWidth) {
        left = rect.left + window.scrollX - popupWidth - 10;
        if (left < 0) {
          left = Math.max(10, rect.left + window.scrollX);
          top = rect.bottom + window.scrollY + 10;
        }
      }
      if (top + popupHeight > window.innerHeight + window.scrollY) {
        top = Math.max(10, rect.top + window.scrollY - popupHeight - 10);
      }

      const actualIndex = (currentPage - 1) * pageSize + rowIndex;
      setActivePopup({
        rowIndex,
        parts: sortedData[actualIndex].parts,
        position: { top, left },
      });
    }
  };

  const PartsPopup = ({ parts, position, onClose }) => {
    const popupRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (popupRef.current && !popupRef.current.contains(event.target)) {
          onClose();
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [onClose]);

    if (!parts || parts.length === 0) return null;
    return (
      <div
        ref={popupRef}
        className="fixed z-50 bg-white rounded-lg shadow-xl border border-gray-200 w-80 animate-in fade-in zoom-in-95 duration-150"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
          maxHeight: '80vh',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-3 border-b border-gray-100 bg-gray-50">
          <h3 className="font-medium text-gray-700">Parts Details</h3>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-200 transition-colors"
          >
            <X size={16} className="text-gray-500" />
          </button>
        </div>
        <div
          className="p-3 overflow-y-auto"
          style={{ maxHeight: 'calc(80vh - 110px)' }}
        >
          <div className="space-y-2">
            <div className="grid grid-cols-12 text-xs font-medium text-gray-500 pb-1 border-b">
              <div className="col-span-7">Part Name</div>
              <div className="col-span-3 text-right">Quantity</div>
              <div className="col-span-2 text-right">Unit</div>
            </div>
            {parts.map((part, idx) => (
              
              <div
                key={idx}
                className={cn(
                  'grid grid-cols-12 text-sm py-2 px-1 rounded-md',
                  idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                )}
              >
                <div
                  className="col-span-7 font-medium text-gray-700 truncate"
                  title={part.name}
                >
                  {part.name}
                </div>
                <div className="col-span-3 text-right text-gray-700">
                  {part.quantity?.toLocaleString?.() ?? part.quantity}
                </div>
                <div className="col-span-2 text-right text-gray-500 text-xs">
                  {part.unit || 'pcs'}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="p-3 border-t border-gray-100 bg-gray-50 text-xs text-gray-500 flex justify-between">
          <span>Total Parts: {parts.length}</span>
          <span>
            Total Quantity:{' '}
            {parts.reduce((sum, p) => sum + (p.quantity || 0), 0).toLocaleString()}
          </span>
        </div>
      </div>
    );
  };

  const getSortIcon = (key) => {
    const sortConfig = sortConfigs.find((c) => c.key === key);
    if (!sortConfig) {
      return <ArrowUpDown size={16} className="ml-1 text-gray-400" />;
    }
    if (sortConfig.direction === 'ascending') {
      return (
        <div className="flex items-center">
          <ArrowUp size={16} className="ml-1 text-accent-600" />
          {sortConfigs.length > 1 && (
            <span className="ml-0.5 text-xs text-accent-600">
              {sortConfig.order}
            </span>
          )}
        </div>
      );
    }
    if (sortConfig.direction === 'descending') {
      return (
        <div className="flex items-center">
          <ArrowDown size={16} className="ml-1 text-accent-600" />
          {sortConfigs.length > 1 && (
            <span className="ml-0.5 text-xs text-accent-600">
              {sortConfig.order}
            </span>
          )}
        </div>
      );
    }
    return <ArrowUpDown size={16} className="ml-1 text-gray-400" />;
  };

  // 1) apply filters
  const applyFilter = (row, filter) => {
    const { column, operator, value } = filter;
    if (operator === 'isEmpty') {
      const cellValue = row[column];
      return !cellValue || String(cellValue).trim() === '';
    }
    if (operator === 'isNotEmpty') {
      const cellValue = row[column];
      return cellValue && String(cellValue).trim() !== '';
    }
    if (!value) return true;

    // Handle parts array (part_numbers column)
    if (column === 'part_numbers') {
      const parts = row[column];

      if (!parts || !Array.isArray(parts)) return !value;

      const filterValue = String(value).toLowerCase();

      // Numeric operators - check against qty field of each part
      if (operator === 'greaterThan' || operator === 'lessThan') {
        const numericFilter = parseFloat(value);
        if (isNaN(numericFilter)) return true;

        return parts.some((part) => {
          const partQty = part?.qty ?? part?.quantity ?? 0;
          if (operator === 'greaterThan') {
            return partQty > numericFilter;
          }
          return partQty < numericFilter;
        });
      }

      // Text operators - check against part_number field of each part
      return parts.some((part) => {
        const partNumber = String(part?.part_number ?? part?.name ?? '').toLowerCase();
        switch (operator) {
          case 'contains':
            return partNumber.includes(filterValue);
          case 'equals':
            return partNumber === filterValue;
          case 'is not':
            return partNumber !== filterValue;
          case 'startsWith':
            return partNumber.startsWith(filterValue);
          case 'endsWith':
            return partNumber.endsWith(filterValue);
          default:
            return true;
        }
      });
    }

    if (dateColumns.includes(column)) {
      const rowDate = new Date(row[column]);
      const filterDate = new Date(value);
      if (isNaN(rowDate.getTime()) || isNaN(filterDate.getTime())) return false;
      switch (operator) {
        case 'contains':
          return rowDate.toISOString().split('T')[0].includes(value);
        case 'equals':
          return rowDate.toISOString().split('T')[0] === filterDate.toISOString().split('T')[0];
        case 'is not':
          return rowDate.toISOString().split('T')[0] !== filterDate.toISOString().split('T')[0];
        case 'startsWith':
          return rowDate.toISOString().split('T')[0].startsWith(value);
        case 'endsWith':
          return rowDate.toISOString().split('T')[0].endsWith(value);
        case 'greaterThan':
          return rowDate > filterDate;
        case 'lessThan':
          return rowDate < filterDate;
        default:
          return true;
      }
    }

    const cellValue = String(row[column] || '').toLowerCase();
    const filterValue = String(value).toLowerCase();
    switch (operator) {
      case 'contains':
        return cellValue.includes(filterValue);
      case 'equals':
        return cellValue === filterValue;
      case 'is not':
        return cellValue !== filterValue;
      case 'startsWith':
        return cellValue.startsWith(filterValue);
      case 'endsWith':
        return cellValue.endsWith(filterValue);
      case 'greaterThan':
        return parseFloat(cellValue) > parseFloat(filterValue);
      case 'lessThan':
        return parseFloat(cellValue) < parseFloat(filterValue);
      default:
        return true;
    }
  };

  const filteredData = useMemo(() => {
    let result = data;
    // global search
    if (searchQuery && searchQuery.trim()) {
      const lower = searchQuery.toLowerCase();
      result = result.filter((row) =>
        columns.some((col) => {
          const val = row[col.key];
          if (val == null) return false;
          return String(val).toLowerCase().includes(lower);
        })
      );
    }
    // column filters
    if (filters.length > 0) {
      result = result.filter((row) => {
        if (filterLogic === 'AND') {
          return filters.every((f) => applyFilter(row, f));
        } else {
          return filters.some((f) => applyFilter(row, f));
        }
      });
    }
    return result;
  }, [data, columns, searchQuery, filters, filterLogic]);

  // 2) sort
  const sortedData = useMemo(() => {
    if (sortConfigs.length === 0) return filteredData;
    const orderedConfigs = [...sortConfigs].sort((a, b) => a.order - b.order);

    return [...filteredData].sort((a, b) => {
      for (const config of orderedConfigs) {
        const { key, direction } = config;
        const aVal = a[key];
        const bVal = b[key];

        // Handle null/undefined/empty values
        // When sorting in ascending order, null values come first
        // When sorting in descending order, null values come last
        if (aVal == null || aVal === '') {
          if (bVal == null || bVal === '') return 0; // Both are null/empty, consider them equal
          return direction === 'ascending' ? -1 : 1; // Null values first in ascending, last in descending
        }
        if (bVal == null || bVal === '') {
          return direction === 'ascending' ? 1 : -1; // Null values first in ascending, last in descending
        }
        if (aVal === bVal) continue;

        // date col?
        if (dateColumns.includes(key)) {
          const aDate = new Date(aVal);
          const bDate = new Date(bVal);
          if (direction === 'ascending') {
            return aDate - bDate;
          } else {
            return bDate - aDate;
          }
        }

        // try numeric
        const aStr = String(aVal);
        const bStr = String(bVal);
        const aNum = parseFloat(aStr);
        const bNum = parseFloat(bStr);

        if (!isNaN(aNum) && !isNaN(bNum)) {
          if (direction === 'ascending') {
            return aNum - bNum;
          } else {
            return bNum - aNum;
          }
        } else {
          // fallback to string compare
          if (direction === 'ascending') {
            return aStr.localeCompare(bStr);
          } else {
            return bStr.localeCompare(aStr);
          }
        }
      }
      return 0;
    });
  }, [filteredData, sortConfigs]);

  // 3) pagination
  const totalPages = Math.ceil(sortedData.length / pageSize);
  const currentData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [sortedData, currentPage, pageSize]);

  if (!data.length) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <p className="text-gray-500">No data available</p>
      </div>
    );
  }

  if (data.length > 0 && sortedData.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <p className="text-gray-500">No matching results found</p>
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('clearSearch'))}
          className="mt-4 px-4 py-2 bg-accent-600 text-white rounded-md hover:bg-accent-700 transition-colors"
        >
          Clear Search
        </button>
      </div>
    );
  }

  // Pagination helpers
  const goToPage = (page) => {
    if (page < 1) page = 1;
    if (page > totalPages) page = totalPages;
    setCurrentPage(page);
  };
  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };
  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  return (
    <div className="w-full overflow-hidden rounded-lg shadow-sm border border-gray-200">
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 text-sm text-gray-500 flex justify-end items-center">
        <div>
          Showing {currentData.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} -{' '}
          {Math.min(currentPage * pageSize, sortedData.length)} of {sortedData.length} entries
          {filters.length > 0 && (
            <span className="ml-2">
              (Filtered by {filters.length}{' '}
              {filters.length === 1 ? 'condition' : 'conditions'} with {filterLogic} logic)
            </span>
          )}
          {sortConfigs.length > 0 && (
            <span className="ml-2">
              (Sorted by {sortConfigs.length}{' '}
              {sortConfigs.length === 1 ? 'column' : 'columns'})
            </span>
          )}
        </div>
      </div>

      <div className="overflow-x-auto" style={{ maxWidth: '100%' }}>
        <table className="w-full bg-white min-w-max">
          <thead className="sticky top-0 bg-gray-50 z-10">
            <tr className="border-b border-gray-200">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={cn(
                    'px-4 py-3 text-left text-sm font-medium text-gray-700',
                    'cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap',
                    sortConfigs.some((c) => c.key === column.key) && 'bg-gray-100'
                  )}
                  onClick={() => handleSort(column.key)}
                >
                  <div className="flex items-center">
                    {column.label}
                    {getSortIcon(column.key)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {currentData.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={cn(
                  (row.po_review_required > 10 || (row.title?.length ?? 0) < 5)
                    ? "bg-amber-100 hover:bg-amber-100"
                    : "hover:bg-gray-50",
                  "transition-colors cursor-pointer"
                )}
                onClick={(e) => handleRowClickInternal(rowIndex, e)}
              >
                {columns.map((column) => {
                  const cellKey = `${rowIndex}-${column.key}`;

                  // Example: you could show parts differently if needed
                  if (
                    column.key === 'numberOfParts' &&
                    row.part_numbers &&
                    row.part_numbers.length > 0
                  ) {
                    return (
                      <td
                        key={cellKey}
                        className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap"
                      >
                        <div
                          className="cursor-pointer text-blue-600 font-medium hover:text-blue-800 hover:underline flex items-center"
                          data-has-parts="true"
                          onClick={(e) => handlePartsClick(rowIndex, e)}
                        >
                          <span>{row.part_numbers.length}</span>
                        </div>
                      </td>
                    );
                  }

                  return (
                    <td
                      key={cellKey}
                      className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap"
                    >
                      {column.render ? column.render(row) : row[column.key]}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 text-sm flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="p-1 border border-gray-300 rounded text-sm"
            >
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
              <option value={200}>200 per page</option>
              <option value={500}>500 per page</option>
            </select>
            <span className="text-gray-500">
              Page {currentPage} of {totalPages}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => goToPage(1)}
              disabled={currentPage === 1}
              className={`px-2 py-1 rounded ${
                currentPage === 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
            >
              First
            </button>
            <button
              onClick={prevPage}
              disabled={currentPage === 1}
              className={`px-2 py-1 rounded ${
                currentPage === 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
            >
              Previous
            </button>
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => goToPage(pageNum)}
                    className={`w-8 h-8 flex items-center justify-center rounded ${
                      currentPage === pageNum
                        ? 'bg-accent-600 text-white'
                        : 'text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={nextPage}
              disabled={currentPage === totalPages}
              className={`px-2 py-1 rounded ${
                currentPage === totalPages
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
            >
              Next
            </button>
            <button
              onClick={() => goToPage(totalPages)}
              disabled={currentPage === totalPages}
              className={`px-2 py-1 rounded ${
                currentPage === totalPages
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
            >
              Last
            </button>
          </div>
        </div>
      )}

      {/* The floating parts popup, if open */}
      {activePopup && activePopup.parts && activePopup.position && (
        <PartsPopup
          parts={activePopup.parts}
          position={activePopup.position}
          onClose={() => setActivePopup(null)}
        />
      )}
    </div>
  );
};

export default DataTable;
